import { getStorage } from '@/store/storage';
import { affiliateApi } from '@/lib/api/affiliate';

const KEY = 'pending-affiliate-receipt';
const valid = value => typeof value === 'string' && value.length <= 256 && /^[a-z0-9-]+\.\d{4}-.*\.[A-Za-z0-9_-]{16}$/.test(value);
export function createPendingAffiliate(storage, api) {
  let receipt = null, hydration, revision = 0, writes = Promise.resolve();
  const listeners = new Set(), claims = new Map();
  const notify = () => listeners.forEach(fn => fn());
  const persist = value => {
    writes = writes.then(() => value ? storage.setItem(KEY, value) : storage.removeItem(KEY)).catch(() => {});
    return writes;
  };
  const read = async () => {
    if (!hydration) {
      hydration = Promise.resolve().then(() => storage.getItem(KEY)).catch(() => null).then(value => {
        receipt = valid(value) ? value : null;
      });
    }
    await hydration;
    return receipt;
  };
  return {
    read,
    subscribe(fn) { listeners.add(fn); return () => listeners.delete(fn); },
    async capture(slug) {
      const ownRevision = ++revision;
      await read();
      try {
        const result = await api.captureNativeClick(slug);
        if (!valid(result?.receipt) || ownRevision !== revision) return false;
        receipt = result.receipt;
        await persist(receipt);
        notify();
        return true;
      } catch { return false; } // Failed taps do not destroy a retained receipt.
    },
    async claim(sessionKey, isRequestCurrent) {
      const value = await read();
      if (!value || !sessionKey || !isRequestCurrent()) return false;
      const key = sessionKey + value;
      if (claims.has(key)) return claims.get(key);
      const request = (async () => {
        try {
          const result = await api.claimNativeClick(value, isRequestCurrent);
          if (result?.consumed && isRequestCurrent() && receipt === value) {
            receipt = null;
            await persist(null);
          }
          return !!result?.consumed;
        } catch { return false; } // Keep it for a later foreground/sign-in retry.
        finally { claims.delete(key); }
      })();
      claims.set(key, request);
      return request;
    },
  };
}
export const pendingAffiliate = createPendingAffiliate({
  getItem: key => getStorage().getItem(key),
  setItem: (key, value) => getStorage().setItem(key, value),
  removeItem: key => getStorage().removeItem(key),
}, affiliateApi);
